//src/hooks/PlayerContext.js
// Enhanced PlayerContext with proper AI Radio Mode integration and token management
// Extracted hooks: useSpotifyAPI, usePlaybackControls, usePlaylistManager, useDJBreaks
import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { useRadioMode } from './useRadioMode';
import { useTokenManager } from './useTokenManager';
import { useSpotifyAPI } from './useSpotifyAPI';
import { usePlaybackControls } from './usePlaybackControls';
import { usePlaylistManager } from './usePlaylistManager';
import { useDJBreaks } from './useDJBreaks';
import { useAutoQueue } from './useAutoQueue';
import { useListeningTracker } from './useListeningTracker';
import logger from '../services/logger';

// Initialize flags for player state
if (typeof window !== 'undefined') {
  window._playerStateFlags = {
    isChangingTrack: false,
    isSeeking: false,
    trackEndProcessed: false
  };
}

// Item type helpers
const isSegmentItem = (item) => item && item.type === 'segment';
const isTrackItem = (item) => item && item.type !== 'segment';

// Initial state - keeping everything
const initialState = {
  // Platform and authentication
  platform: null,
  accessToken: null,
  tokenExpiry: null,
  deviceId: null,

  // Core player state
  currentTrackIndex: 0,
  isPlaying: false,
  progressMs: 0,
  durationMs: 0,

  // Playlist and tracks
  playlist: { name: '', tracks: [] },
  previousPlaylist: null, // For restoration
  contextVersion: 0, // Increments on playlist/track changes - single source of truth

  // Playback options
  radioModeActive: false,
  shuffleMode: false,
  shuffleOrder: null, // Index-permutation array — non-destructive shuffle, never mutates tracks
  repeatMode: 'off',
  playbackRate: 1.0,

  // UI state
  loading: false,
  error: null,
  connectionStatus: 'disconnected',

  // User data
  userData: null,
  mediaData: null
};

// Action types
const ActionTypes = {
  SET_PLATFORM: 'SET_PLATFORM',
  SET_AUTH: 'SET_AUTH',
  UPDATE_ACCESS_TOKEN: 'UPDATE_ACCESS_TOKEN',
  SET_DEVICE: 'SET_DEVICE',
  SET_PLAYLIST: 'SET_PLAYLIST',
  UPDATE_PLAYLIST: 'UPDATE_PLAYLIST',
  APPEND_TRACKS: 'APPEND_TRACKS',
  REORDER_PLAYLIST: 'REORDER_PLAYLIST',
  SET_TRACK_INDEX: 'SET_TRACK_INDEX',
  SET_PLAYBACK_STATE: 'SET_PLAYBACK_STATE',
  SET_PROGRESS: 'SET_PROGRESS',
  SET_PLAYBACK_OPTION: 'SET_PLAYBACK_OPTION',
  SET_UI_STATE: 'SET_UI_STATE'
};

// Enhanced reducer with token management
const playerReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_PLATFORM:
      return {
        ...state,
        platform: action.payload.platform,
        userData: action.payload.userData || state.userData,
        mediaData: action.payload.mediaData || state.mediaData
      };

    case ActionTypes.SET_AUTH:
      return {
        ...state,
        accessToken: action.payload.token,
        tokenExpiry: action.payload.expiry || null,
        connectionStatus: action.payload.token ? 'connected' : 'disconnected'
      };

    case ActionTypes.UPDATE_ACCESS_TOKEN:
      return {
        ...state,
        accessToken: action.payload,
        tokenExpiry: Date.now() + (3600 * 1000) // 1 hour from now
      };

    case ActionTypes.SET_DEVICE:
      return {
        ...state,
        deviceId: action.payload
      };

    case ActionTypes.SET_PLAYLIST:
      return {
        ...state,
        playlist: action.payload.playlist,
        previousPlaylist: action.payload.savePrevious ? state.playlist : state.previousPlaylist,
        currentTrackIndex: action.payload.resetIndex !== false ? 0 : state.currentTrackIndex,
        progressMs: action.payload.resetProgress !== false ? 0 : state.progressMs,
        contextVersion: state.contextVersion + 1
      };

    case ActionTypes.UPDATE_PLAYLIST: {
      return {
        ...state,
        playlist: {
          ...state.playlist,
          tracks: action.payload.tracks
        },
        contextVersion: state.contextVersion + 1
      };
    }

    // APPEND_TRACKS: reducer reads current state.playlist.tracks to avoid stale closures
    // addTracksToPlaylist dispatches this instead of pre-building the array in the callback
    case ActionTypes.APPEND_TRACKS: {
      const { tracks: newTracks, position } = action.payload;
      const updatedTracks = [...state.playlist.tracks];
      let insertIndex;

      if (position === 'next') {
        insertIndex = state.currentTrackIndex + 1;
        updatedTracks.splice(insertIndex, 0, ...newTracks);
      } else if (typeof position === 'number') {
        insertIndex = position;
        updatedTracks.splice(position, 0, ...newTracks);
      } else {
        // 'end' (default)
        insertIndex = state.playlist.tracks.length;
        updatedTracks.push(...newTracks);
      }

      return {
        ...state,
        playlist: { ...state.playlist, tracks: updatedTracks },
        contextVersion: state.contextVersion + 1
      };
    }

    case ActionTypes.REORDER_PLAYLIST: {
      return {
        ...state,
        playlist: {
          ...state.playlist,
          tracks: action.payload.tracks
        },
        currentTrackIndex: action.payload.newIndex !== undefined ? action.payload.newIndex : state.currentTrackIndex,
        progressMs: action.payload.preserveProgress ? state.progressMs : 0,
        contextVersion: state.contextVersion
      };
    }

    case ActionTypes.SET_TRACK_INDEX:
      const trackIndex = typeof action.payload === 'object' ? action.payload.index : action.payload;
      const preserveProgress = action.payload?.preserveProgress || false;
      const preserveContext = action.payload?.preserveContext || false;
      return {
        ...state,
        currentTrackIndex: trackIndex,
        progressMs: preserveProgress ? state.progressMs : 0,
        contextVersion: preserveContext ? state.contextVersion : state.contextVersion + 1
      };

    case ActionTypes.SET_PLAYBACK_STATE:
      return {
        ...state,
        isPlaying: action.payload
      };

    case ActionTypes.SET_PROGRESS:
      return {
        ...state,
        progressMs: action.payload.progressMs,
        durationMs: action.payload.durationMs || state.durationMs
      };

    case ActionTypes.SET_PLAYBACK_OPTION:
      return {
        ...state,
        [action.payload.option]: action.payload.value
      };

    case ActionTypes.SET_UI_STATE:
      return {
        ...state,
        ...action.payload
      };

    default:
      return state;
  }
};

// Create context
const PlayerContext = createContext();

// Enhanced Provider component with AI Radio Mode and Token Management
export function PlayerProvider({ children }) {
  const [state, dispatch] = useReducer(playerReducer, initialState);
  const playerInstanceRef = useRef(null);
  const lastActionTimeRef = useRef(0);
  const segmentPlaybackRef = useRef({ isPlaying: false, isPreloading: false, placardId: null });
  const segmentCacheRef = useRef({}); // Cache for preloaded segment audio
  const fadeIntervalRef = useRef(null); // Active fade interval — prevents overlapping fades
  const playedSinceBreakRef = useRef([]); // Tracks actually heard since last DJ break
  const adLibFiredRef = useRef(new Set());          // Track indices where ad_lib already evaluated
  const hotMicFiredRef = useRef(new Set());          // Track indices where hot_mic already evaluated
  const producerFillScheduledRef = useRef(false);   // Whether producer_fill is queued for next transition
  const playlistTracksRef = useRef(state.playlist.tracks); // always-current tracks for useDJBreaks stale-closure guard
  const currentTrackIndexRef = useRef(0);                  // always-current idx for stale-tick guard

  // Add token manager
  const { tokens, getValidToken, refreshSpotifyToken, refreshYouTubeToken } = useTokenManager();

  // === EXTRACTED HOOK: Spotify API ===
  const spotifyAPI = useSpotifyAPI(state, dispatch, {
    getValidToken,
    refreshSpotifyToken,
    refreshYouTubeToken
  });

  // Core action creators (kept here - tightly coupled to state)
  const setPlatformData = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_PLATFORM, payload: data });
  }, []);

  const setAuthToken = useCallback((token, expiry) => {
    dispatch({ type: ActionTypes.SET_AUTH, payload: { token, expiry } });
  }, []);

  const updateAccessToken = useCallback((newToken) => {
    dispatch({
      type: ActionTypes.UPDATE_ACCESS_TOKEN,
      payload: newToken
    });
    logger.debug('🔄 Updated access token in player context');
  }, []);

  // Watch for token changes and update player context
  React.useEffect(() => {
    if (tokens.spotify.token && tokens.spotify.token !== state.accessToken) {
      updateAccessToken(tokens.spotify.token);
    }
  }, [tokens.spotify.token, state.accessToken, updateAccessToken]);

  // Keep playlistTracksRef current so useDJBreaks 80% block never reads a stale snapshot
  useEffect(() => { playlistTracksRef.current = state.playlist.tracks; }, [state.playlist.tracks]);

  const setDeviceId = useCallback((deviceId) => {
    dispatch({ type: ActionTypes.SET_DEVICE, payload: deviceId });
  }, []);

  const setPlaybackOption = useCallback((option, value) => {
    dispatch({ type: ActionTypes.SET_PLAYBACK_OPTION, payload: { option, value } });
  }, []);

  const setUIState = useCallback((stateUpdate) => {
    dispatch({ type: ActionTypes.SET_UI_STATE, payload: stateUpdate });
  }, []);

  // Store radioMode ref early (needed by extracted hooks)
  const radioModeRef = useRef(null);
  // Auto-queue check ref (set after useAutoQueue is instantiated)
  const checkQueueRef = useRef(null);

  // === EXTRACTED HOOK: Playback Controls ===
  // Need setCurrentTrackIndex first for handleNextTrack/handlePrevTrack
  const setCurrentTrackIndex = useCallback((index) => {
    const validIndex = Math.max(0, Math.min(index, (state.playlist.tracks?.length || 1) - 1));

    dispatch({ type: ActionTypes.SET_TRACK_INDEX, payload: validIndex });

    currentTrackIndexRef.current = validIndex; // update BEFORE clearTriggerFlags so stale tick sees new idx
    // Clear placard trigger flags for the new track (delegated to djBreaks)
    if (djBreaksRef.current?.clearTriggerFlags) {
      djBreaksRef.current.clearTriggerFlags();
    }
    adLibFiredRef.current.clear();
    hotMicFiredRef.current.clear();
    producerFillScheduledRef.current = false;
  }, [state.playlist.tracks, state.currentTrackIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const playbackControls = usePlaybackControls(state, dispatch, {
    playerInstanceRef,
    lastActionTimeRef,
    segmentPlaybackRef,
    radioModeRef,
    producerFillScheduledRef,
    fadeIntervalRef,
    makeAuthenticatedRequest: spotifyAPI.makeAuthenticatedRequest,
    setPlaybackOption,
    setCurrentTrackIndex
  });

  // === EXTRACTED HOOK: DJ Breaks ===
  const djBreaks = useDJBreaks(state, dispatch, {
    playerInstanceRef,
    segmentPlaybackRef,
    segmentCacheRef,
    playedSinceBreakRef,
    adLibFiredRef,
    hotMicFiredRef,
    producerFillScheduledRef,
    radioModeRef,
    fadeVolume: playbackControls.fadeVolume,
    setCurrentTrackIndex,
    checkQueue: null, // Set via ref after useAutoQueue is created
    checkQueueRef,
    playlistTracksRef,
    currentTrackIndexRef,
  });

  // Store djBreaks ref for setCurrentTrackIndex to call clearTriggerFlags
  const djBreaksRef = useRef(djBreaks);
  useEffect(() => {
    djBreaksRef.current = djBreaks;
  }, [djBreaks]);

  // Stable callback — always accesses djBreaks via ref, safe to use as dep
  const clearDJCache = useCallback(() => {
    djBreaksRef.current?.clearTriggerFlags?.(false); // next track changed, current track didn't — preserve 80% count
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === EXTRACTED HOOK: Playlist Manager ===
  const playlistManager = usePlaylistManager(state, dispatch, {
    radioModeRef,
    setCurrentTrackIndex,
    setPlaybackState: playbackControls.setPlaybackState,
    clearDJCache
  });

  // === EXTRACTED HOOK: Auto Queue (vector recommendation engine) ===
  const autoQueue = useAutoQueue(state, dispatch, {
    radioModeRef,
    addTracksToPlaylist: playlistManager.addTracksToPlaylist,
  });

  // Bridge autoQueue.checkQueue to useDJBreaks via ref
  useEffect(() => {
    checkQueueRef.current = autoQueue.checkQueue;
  }, [autoQueue.checkQueue]);

  // === EXTRACTED HOOK: Listening Tracker ===
  useListeningTracker({
    progressMs: state.progressMs,
    durationMs: state.durationMs,
    isPlaying: state.isPlaying,
    currentTrackIndex: state.currentTrackIndex,
    playlist: state.playlist,
    platform: state.platform,
  });

  // Single source of truth - get playlist context with version
  const getPlaylistContext = useCallback(() => ({
    tracks: state.playlist.tracks,
    currentIndex: state.currentTrackIndex,
    currentTrack: state.playlist.tracks[state.currentTrackIndex] || null,
    nextTrack: state.playlist.tracks[state.currentTrackIndex + 1] || null,
    version: state.contextVersion
  }), [state.playlist.tracks, state.currentTrackIndex, state.contextVersion]);

  const setPlayerInstance = useCallback((instance) => {
    playerInstanceRef.current = instance;
    if (typeof window !== 'undefined') {
      window._playerInstance = instance;
    }
  }, []);

  // Create context object for radio mode hook
  const radioContext = {
    state,
    addTracksToPlaylist: playlistManager.addTracksToPlaylist,
    updatePlaylistTracks: playlistManager.updatePlaylistTracks,
    setPlaybackOption,
    playShowOpen: djBreaks.playShowOpen,
    setAvailableModules: djBreaks.setAvailableModules,
    setRadioStartTime: djBreaks.setRadioStartTime,
    autoQueue,
  };

  // Initialize AI Radio Mode
  const radioMode = useRadioMode(radioContext);

  // Store radioMode in ref for use in callbacks
  useEffect(() => {
    radioModeRef.current = radioMode;
  }, [radioMode]);

  // Enhanced radio mode integration
  const toggleRadioMode = useCallback(() => {
    const newState = radioMode.toggle();
    setPlaybackOption('radioModeActive', newState);
    return newState;
  }, [radioMode, setPlaybackOption]);

  // Sync radio mode state with context
  const syncedRadioMode = {
    ...radioMode,
    isActive: state.radioModeActive || radioMode.isActive
  };

  // Override handleSeek to use djBreaks.setProgress instead of the null ref
  const handleSeek = useCallback(async (positionMs) => {
    if (window._playerStateFlags.isChangingTrack) return;

    window._playerStateFlags.isSeeking = true;

    djBreaks.setProgress(positionMs, state.durationMs);

    if (state.platform === 'spotify' && state.deviceId) {
      try {
        await spotifyAPI.makeAuthenticatedRequest(
          `https://api.spotify.com/v1/me/player/seek?position_ms=${positionMs}&device_id=${state.deviceId}`,
          { method: 'PUT' },
          'spotify'
        );
      } catch (err) {
        logger.error("Seek error:", err);
      } finally {
        setTimeout(() => {
          window._playerStateFlags.isSeeking = false;
        }, 300);
      }
    }
  }, [state.platform, state.deviceId, state.durationMs, djBreaks, spotifyAPI]);

  // Enhanced context value with AI Radio Mode and Token Management
  const value = {
    state: {
      ...state,
      radioModeActive: syncedRadioMode.isActive // Sync radio mode state
    },

    // Token management
    tokens,
    makeAuthenticatedRequest: spotifyAPI.makeAuthenticatedRequest,
    getValidToken: spotifyAPI.getValidToken,
    refreshSpotifyToken,
    refreshYouTubeToken,
    updateAccessToken,

    // Core state setters
    setPlatformData,
    setAuthToken,
    setDeviceId,
    setPlaylist: playlistManager.setPlaylist,
    setCurrentTrackIndex,
    setPlaybackState: playbackControls.setPlaybackState,
    setProgress: djBreaks.setProgress,
    setPlaybackOption,
    setUIState,
    setPlayerInstance,

    // Player control
    handlePlayPause: playbackControls.handlePlayPause,
    handleNextTrack: playbackControls.handleNextTrack,
    handlePrevTrack: playbackControls.handlePrevTrack,
    handleSeek,

    // Playlist management
    addTrackToPlaylist: playlistManager.addTrackToPlaylist,
    addTracksToPlaylist: playlistManager.addTracksToPlaylist,
    playTrack: playlistManager.playTrack,
    reorderPlaylistTracks: playlistManager.reorderPlaylistTracks,
    removeTrackFromPlaylist: playlistManager.removeTrackFromPlaylist,
    restorePreviousPlaylist: playlistManager.restorePreviousPlaylist,
    clearPlaylist: playlistManager.clearPlaylist,
    createPlaylistFromTracks: playlistManager.createPlaylistFromTracks,

    // Item type helpers
    isSegmentItem,
    isTrackItem,

    // Enhanced features
    normalizeTrackFormat: playlistManager.normalizeTrackFormat,
    playTrackNext: playlistManager.playTrackNext,
    toggleShuffle: playbackControls.toggleShuffle,
    toggleRepeat: playbackControls.toggleRepeat,
    setPlaybackRate: playbackControls.setPlaybackRate,
    fadeVolume: playbackControls.fadeVolume,

    // AI Radio Mode integration
    radioMode: syncedRadioMode,
    toggleRadioMode,

    // Vector recommendation engine
    autoQueue,

    // Single source of truth - context with version
    getPlaylistContext,

    // Expose dispatch for advanced usage
    dispatch
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
}

// Custom hook
export function usePlayerContext() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayerContext must be used within a PlayerProvider');
  }
  return context;
}
